import { useCallback, useEffect, useRef, useState } from 'react';
import { KULCINA_EMPTY, relativeTimeSr } from '@movienight/shared';
import { DISPLAY } from './bgData';
import { paint } from './display';
import type { KulcinaSync } from './useKulcina';

/**
 * The editor is the building, not a dialog: kulcina.png and its window lights at
 * one integer zoom, with the display canvas registered onto the face by the
 * offsets the layer stack already carries. Pixel art scales losslessly, so it is
 * the same Kulčina, just enormous — and it runs off the bottom of the screen on
 * purpose, because the display sits near the crown and only that has to be
 * reachable.
 *
 * This file owns geometry and pointers only. Who else is drawing, what gets
 * sent, and what happens when two strokes collide all live in useKulcina.
 */

/** Sprite-local offsets: kulcina.png is at (1019,3376), its lights at (1055,3386), the display at (1065,3385). */
const TOWER = { w: 139, h: 293 };
const WIN = { dx: 36, dy: 10, w: 64, h: 93 };
const DISP = { dx: 46, dy: 9 };

/** How often the "pre 2 sata" in the header re-reads the clock. */
const CLOCK_MS = 30_000;

export function KulcinaEditor({
  open,
  kulcina,
  onClose,
}: {
  open: boolean;
  kulcina: KulcinaSync;
  onClose: () => void;
}) {
  const { bits, artist, watch, paintCell, endStroke, replaceAll } = kulcina;

  const wrapRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const towerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLImageElement>(null);
  const winRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const closingRef = useRef<number | null>(null);

  const render = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) paint(ctx, bits, true);
  }, [bits]);

  // Every change to the grid repaints here, whoever made it — your own stroke,
  // someone else's arriving over the socket, or a commit landing.
  useEffect(() => {
    render();
    return watch(render);
  }, [render, watch]);

  /**
   * Integer zoom only; a fractional one blurs the art. Height picks it — the
   * display is 15 wide and 32 tall, so it always binds first — and width only
   * clamps, so the tower can never grow wider than the screen.
   */
  const layout = useCallback(() => {
    const tower = towerRef.current;
    const head = headRef.current;
    if (!tower || !head) return;

    let z = Math.floor((window.innerHeight * 0.72) / DISPLAY.h);
    z = Math.min(z, Math.floor((window.innerWidth - 48) / TOWER.w), 10);
    z = Math.max(4, z);

    const box = (el: HTMLElement | null, x: number, y: number, w: number, h: number) => {
      if (!el) return;
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;
    };
    box(bodyRef.current, 0, 0, TOWER.w * z, TOWER.h * z);
    box(winRef.current, WIN.dx * z, WIN.dy * z, WIN.w * z, WIN.h * z);
    box(canvasRef.current, DISP.dx * z, DISP.dy * z, DISPLAY.w * z, DISPLAY.h * z);

    // Anchored by the DISPLAY, not the building: at this zoom the tower is
    // ~2000px tall and overflows on purpose, but the grid has to clear the
    // header and stay entirely reachable.
    const top = Math.round(head.getBoundingClientRect().height + 18 - DISP.dy * z);
    box(tower, Math.round((window.innerWidth - TOWER.w * z) / 2), top, TOWER.w * z, TOWER.h * z);
    // Measured rather than a guessed 110vh: the resting top and the height both
    // move with the zoom.
    tower.style.setProperty('--kul-drop', `${window.innerHeight - top + 8}px`);
  }, []);

  // Classes are driven imperatively rather than through state, because the open
  // sequence depends on a forced reflow landing *between* two class changes —
  // React would batch them into one style pass and the tower would just appear.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    if (open) {
      if (closingRef.current !== null) {
        window.clearTimeout(closingRef.current);
        closingRef.current = null;
      }
      wrap.classList.add('open');
      wrap.classList.add('rising');
      layout();
      render();
      void wrap.offsetHeight; // commit the off-screen position first
      wrap.classList.remove('rising');
      return;
    }

    if (!wrap.classList.contains('open') || closingRef.current !== null) return;
    wrap.classList.add('rising');
    const ms = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 520;
    closingRef.current = window.setTimeout(() => {
      wrap.classList.remove('open');
      wrap.classList.remove('rising');
      closingRef.current = null;
    }, ms);
  }, [open, layout, render]);

  useEffect(() => {
    const onResize = () => {
      if (wrapRef.current?.classList.contains('open')) layout();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && wrapRef.current?.classList.contains('open')) onClose();
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onKey);
    };
  }, [layout, onClose]);

  // The attribution line arrives after the first fetch and changes the header's
  // height, and the header's height is what the tower is anchored to.
  useEffect(() => {
    if (wrapRef.current?.classList.contains('open')) layout();
  }, [artist, layout]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => window.clearInterval(timer);
  }, [open]);

  // One cell per 3px pitch INCLUDING its gutter, so the 1px seam between cells
  // is not a dead zone you have to avoid.
  const cellAt = (e: React.PointerEvent<HTMLCanvasElement>): number => {
    const cv = canvasRef.current;
    if (!cv) return -1;
    const r = cv.getBoundingClientRect();
    const c = Math.floor((((e.clientX - r.left) / r.width) * DISPLAY.w) / DISPLAY.pitch);
    const w = Math.floor((((e.clientY - r.top) / r.height) * DISPLAY.h) / DISPLAY.pitch);
    if (c < 0 || c >= DISPLAY.cols || w < 0 || w >= DISPLAY.rows) return -1;
    return w * DISPLAY.cols + c;
  };

  // The first cell of a stroke decides whether the stroke draws or erases, so
  // dragging back over your own line does not chew holes in it.
  const painting = useRef(false);
  const value = useRef<0 | 1>(1);
  const last = useRef(-1);

  const apply = (i: number) => {
    if (i === last.current || bits[i] === value.current) {
      last.current = i;
      return;
    }
    last.current = i;
    paintCell(i, value.current);
  };

  const stop = () => {
    if (!painting.current) return;
    painting.current = false;
    endStroke();
  };

  const act = (what: 'reset' | 'clear') => {
    replaceAll(what === 'clear' ? KULCINA_EMPTY : DISPLAY.fallback);
  };

  return (
    <div
      className="kul-editor"
      ref={wrapRef}
      role="dialog"
      aria-modal="true"
      aria-label="Kulčina"
      onClick={(e) => {
        if (e.target === wrapRef.current) onClose();
      }}
    >
      <div className="kul-head" ref={headRef}>
        <b>Kulčina</b>
        <span className="kul-hint">
          Svi će videti šta god nacrtaš
          {}
          {artist && (
            <em className="kul-last">
              Poslednji: {artist.nickname}, {relativeTimeSr(artist.at, new Date(now))}
            </em>
          )}
        </span>
      </div>
      <div className="kul-tower" ref={towerRef}>
        <img ref={bodyRef} src="/bg/kulcina.png" alt="" draggable={false} />
        <img ref={winRef} src="/bg/kulcina-lights.png" alt="" draggable={false} />
        <canvas
          id="kulCanvas"
          ref={canvasRef}
          width={DISPLAY.w}
          height={DISPLAY.h}
          onPointerDown={(e) => {
            const i = cellAt(e);
            if (i < 0) return;
            painting.current = true;
            value.current = bits[i] ? 0 : 1;
            last.current = -1;
            e.currentTarget.setPointerCapture(e.pointerId);
            apply(i);
            e.preventDefault();
          }}
          onPointerMove={(e) => {
            if (!painting.current) return;
            const i = cellAt(e);
            if (i >= 0) apply(i);
          }}
          onPointerUp={stop}
          onPointerCancel={stop}
        />
      </div>
      <div className="kul-actions">
        <button type="button" className="slab" onClick={() => act('reset')}>
          Resetuj
        </button>
        <button type="button" className="slab" onClick={() => act('clear')}>
          Izbriši
        </button>
        <button type="button" className="slab" onClick={onClose}>
          Gotovo
        </button>
      </div>
    </div>
  );
}
